import { useEffect, useState } from "react";

import {
  DatePicker,
  TextField,
  Select,
  Checkbox,
  DocumentList,
} from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { CommissionSelection } from "./components";
import {
  getDifficultyTypeList,
  getRequestDocument,
  getForm7,
  saveForm7,
  getEnrollmentDecision,
  getRequest,
  getCommitteesForDocument,
} from "../../api/Request/RequestApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  ToolbarEdit,
  FormCard,
  InstitutionSelect,
  RequestHeader,
} from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import { ValidationFailure, Role } from "../../models";
import {
  Request,
  RequestStatus,
  RequestType,
  DocumentType,
  Form7,
  EnrollmentDecision,
  DocumentCommittee,
} from "../../models/Request";
import { Attachment } from "../../models/Shared/Attachment";
import { ClaimType } from "../../models/User";
import { requestAdminDetailsUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";

export function Form7Edit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const auth = useAuth();

  const createDefault = (): Request => {
    return {
      id: 0,
      requestUID: "",
      code: "",
      statusId: RequestStatus.Draft,
      schoolYearId: 0,
      type: RequestType.RegularEnrollment,
      createdByAdmin: true,
      isOutsideEnrollmentArea: false,
      step: 0,
      subStep: 0,
      parentRemark: null,
    };
  };
  const createDefaultForm7 = (): Form7 => {
    return {
      id: 0,
      requestId: +id,
      sessionDate: new Date(),
      isEarlyEnrollmentApproved: false,
      isEarlyEnrollmentRejected: false,
      isEnrollmentPostponement: false,
      isTemporaryExemptionApproved: false,
      isTemporaryExemptionRejected: false,
      isAppropriateProgram: false,
      isPreparatoryCourse: false,
      hasDifficulties: false,
      request: createDefault(),
      committeesForDocument: [],
    };
  };
  const [item, setItem] = useState<Request>(createDefault());
  const [form7, setForm7] = useState<Form7>(createDefaultForm7());
  const [difficultyTypes, setDifficultyTypes] = useState<CodeBook[]>([]);
  const [enrollmentDecision, setEnrollmentDecision] = useState<
    EnrollmentDecision | undefined
  >();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [initialDocumentCommittees, setInitialDocumentCommittees] =
    useState<DocumentCommittee[]>();

  useEffect(() => {
    (async () => {
      const difficultyTypeList = await getDifficultyTypeList();
      if (difficultyTypeList) {
        setDifficultyTypes(difficultyTypeList);
      }
    })();
  }, []);
  useEffect(() => {
    (async () => {
      setIsEditing(true); // Assume we don't get one.
      if (id && id !== "0") {
        const result = await getForm7(+id);

        if (result) {
          setForm7(result);
          setIsEditing(false);
          setInitialDocumentCommittees(result.committeesForDocument);
          setItem(result.request);
        } else {
          const request = await getRequest(+id);

          if (request) {
            setItem(() => request);
            switch (request.type) {
              case RequestType.EarlyEnrollment:
                handleIsEarlyEnrollmentApprovedChange(true);
                break;
              case RequestType.TemporaryExemption:
                handleIsTemporaryExemptionApprovedChange(true);
                break;
            }

            const committeesForDocument = await getCommitteesForDocument(
              +id,
              DocumentType.Form7
            );

            setInitialDocumentCommittees(committeesForDocument);
            setForm7((prevForm7) => ({
              ...prevForm7,
              request,
              committeesForDocument,
            }));
          }
        }

        const enrollmentDecisionItem = await getEnrollmentDecision(+id);
        if (enrollmentDecisionItem) {
          setEnrollmentDecision(enrollmentDecisionItem);
        }
      }
    })();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (
      item.institutionId &&
      form7.id === 0 &&
      item.type === RequestType.EarlyEnrollment
    ) {
      // There is a problem with the institutionId not being set on the form7
      // although it is shown in the InstitutionSelect. We have not been given
      // any default values for the different request types (for EarlyEnrollment,
      // is the default Approved or Rejected?). We will set an institution.
      // This may be updated based on the selections.
      const d: Form7 = {
        ...form7,
        institutionId: item.institutionId,
      };
      handleUpdate(d);
    }
  }, [item.institutionId]);

  const handleEditingClick = () => {
    setIsEditing(true);
  };
  const handleSessionDateChanged = (val: Date) => {
    const d: Form7 = {
      ...form7,
      sessionDate: val,
    };
    handleUpdate(d);
  };
  const handleSessionPlaceChanged = (val?: string) => {
    const d: Form7 = {
      ...form7,
      sessionPlace: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleIsEarlyEnrollmentApprovedChange = (val: boolean) => {
    const d: Form7 = {
      ...form7,
      isEarlyEnrollmentApproved: val,
      isEarlyEnrollmentRejected: !val,
      isEnrollmentPostponement: false,
      isTemporaryExemptionApproved: false,
      isTemporaryExemptionRejected: false,
      isAppropriateProgram: false,
      isPreparatoryCourse: false,
      institutionId: val ? item.institutionId : undefined,
    };
    handleUpdate(d);
  };
  const handleIsEarlyEnrollmentRejectedChange = (val: boolean) => {
    const d: Form7 = {
      ...form7,
      isEarlyEnrollmentApproved: !val,
      isEarlyEnrollmentRejected: val,
      isEnrollmentPostponement: false,
      isTemporaryExemptionApproved: false,
      isTemporaryExemptionRejected: false,
      isAppropriateProgram: false,
      isPreparatoryCourse: false,
      institutionId: val ? undefined : item.institutionId,
    };
    handleUpdate(d);
  };
  const handleIsEnrollmentPostponementChange = (val: boolean) => {
    const d: Form7 = {
      ...form7,
      isEarlyEnrollmentApproved: false,
      isEarlyEnrollmentRejected: false,
      isEnrollmentPostponement: val,
      isTemporaryExemptionApproved: false,
      isTemporaryExemptionRejected: false,
      isAppropriateProgram: false,
      isPreparatoryCourse: false,
      institutionId: val ? undefined : item.institutionId,
    };
    handleUpdate(d);
  };
  const handleIsTemporaryExemptionApprovedChange = (val: boolean) => {
    const d: Form7 = {
      ...form7,
      isEarlyEnrollmentApproved: false,
      isEarlyEnrollmentRejected: false,
      isEnrollmentPostponement: false,
      isTemporaryExemptionApproved: val,
      isTemporaryExemptionRejected: !val,
      isAppropriateProgram: false,
      isPreparatoryCourse: false,
      institutionId: undefined,
    };
    handleUpdate(d);
  };
  const handleIsTemporaryExemptionRejectedChange = (val: boolean) => {
    const d: Form7 = {
      ...form7,
      isEarlyEnrollmentApproved: false,
      isEarlyEnrollmentRejected: false,
      isEnrollmentPostponement: false,
      isTemporaryExemptionApproved: !val,
      isTemporaryExemptionRejected: val,
      isAppropriateProgram: false,
      institutionId: undefined,
    };
    handleUpdate(d);
  };
  const handleIsAppropriateProgramChange = (val: boolean) => {
    const d: Form7 = {
      ...form7,
      isEarlyEnrollmentApproved: false,
      isEarlyEnrollmentRejected: false,
      isEnrollmentPostponement: false,
      isTemporaryExemptionApproved: false,
      isTemporaryExemptionRejected: false,
      isAppropriateProgram: val,
      institutionId: item.institutionId,
    };
    handleUpdate(d);
  };
  const handleIsPreparatoryCourseChange = (val: boolean) => {
    const d: Form7 = {
      ...form7,
      isPreparatoryCourse: val,
      institutionId: val ? item.institutionId : form7.institutionId,
    };
    handleUpdate(d);
  };
  const handleHasDifficultiesChange = (val: boolean) => {
    const d: Form7 = {
      ...form7,
      hasDifficulties: val,
    };
    handleUpdate(d);
  };
  const handleDifficultyDescriptionChange = (val?: string) => {
    const d: Form7 = {
      ...form7,
      difficultyDescription: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleRulesChange = (val?: string) => {
    const d: Form7 = {
      ...form7,
      rules: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleNNNumberChange = (val?: string) => {
    const d: Form7 = {
      ...form7,
      nnNumber: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleSchoolingDescriptionChange = (val?: string) => {
    const d: Form7 = {
      ...form7,
      schoolingDescription: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleEducationalStatusChange = (val?: string) => {
    const d: Form7 = {
      ...form7,
      educationalStatus: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleAppropriateHelpFormatChange = (val?: string) => {
    const d: Form7 = {
      ...form7,
      appropriateHelpFormat: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleDifficultyTypeChange = (val?: number[]) => {
    const d: Form7 = {
      ...form7,
      difficultyTypes: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleOtherChange = (val?: string) => {
    const d: Form7 = {
      ...form7,
      other: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleInstitutionChange = (val?: number) => {
    const d: Form7 = {
      ...form7,
      institutionId: val && !isNaN(val) ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleDocumentDownload = async (
    doc: Attachment,
    type: DocumentType
  ) => {
    if (doc && doc.id) {
      await getRequestDocument(doc.id, item.id, type);
    }
  };
  const handleUpdate = (item: Form7) => {
    setForm7(item);
    dispatch(setIsEdited());
  };
  const handleCommitteeChange = (documentCommittees: DocumentCommittee[]) => {
    const f: Form7 = {
      ...form7,
      committeesForDocument: documentCommittees,
    };

    handleUpdate(f);
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={
          !isEditing &&
          !enrollmentDecision &&
          ((item.type !== RequestType.RegularEnrollment &&
            item.type !==
              RequestType.RegularEnrollmentForChildrenWithLearningDifficulties) ||
            item.statusId === RequestStatus.OpinionWithRecommendation) &&
          (auth.currentRole?.role === Role.CAS ||
            auth.currentRole?.role === Role.Helpdesk ||
            auth.currentRole?.role === Role.CountyAdministrator ||
            auth.currentRole?.role === Role.CountyLeader ||
            (item.countyCommitteeId &&
              auth
                .getCommitteeMembershipByCommitteeId(item.countyCommitteeId)
                ?.some((v) => v.isPresident)))
            ? "show"
            : "hide"
        }
        pageTitle={t("form7.form7PageTitle")}
        onClickEdit={handleEditingClick}
        errors={errors}
      />
      <RequestHeader request={item} />
      <FormCard cardTitle={t("form7.form7Data")}>
        <Row>
          <Col md={12}>
            <DatePicker
              label={t("decisionProposal.sessionDate")}
              name={"decisionProposal.sessionDate"}
              onChange={(e) =>
                handleSessionDateChanged(new Date(e.target.value))
              }
              value={form7.sessionDate}
              minYear={new Date().getFullYear()}
              plaintext={!isEditing}
              errorText={
                errors?.find((e) => e.propertyName?.startsWith("SessionDate"))
                  ?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <TextField
              label={t("form7.sessionPlace")}
              name={nameof<typeof form7>("sessionPlace")}
              value={form7.sessionPlace}
              onChange={(e) => handleSessionPlaceChanged(e.target.value)}
              plaintext={!isEditing}
              errorText={
                errors?.find((e) => e.propertyName?.startsWith("SessionPlace"))
                  ?.message
              }
            />
          </Col>
        </Row>
        <FormCard cardTitle={t("form7.type")}>
          <Row>
            <Col md={12}>
              <Checkbox
                label={t("form7.isEarlyEnrollmentApproved")}
                name={nameof<typeof form7>("isEarlyEnrollmentApproved")}
                checked={form7.isEarlyEnrollmentApproved}
                onChange={(e) =>
                  handleIsEarlyEnrollmentApprovedChange(e.target.checked)
                }
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("IsEarlyEnrollmentApproved")
                  )?.message
                }
                plaintext={!isEditing}
                disabled={item.type !== RequestType.EarlyEnrollment}
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Checkbox
                label={t("form7.isEarlyEnrollmentRejected")}
                name={nameof<typeof form7>("isEarlyEnrollmentRejected")}
                checked={form7.isEarlyEnrollmentRejected}
                onChange={(e) =>
                  handleIsEarlyEnrollmentRejectedChange(e.target.checked)
                }
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("IsEarlyEnrollmentRejected")
                  )?.message
                }
                plaintext={!isEditing}
                disabled={item.type !== RequestType.EarlyEnrollment}
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Checkbox
                label={t("form7.isEnrollmentPostponement")}
                name={nameof<typeof form7>("isEnrollmentPostponement")}
                checked={form7.isEnrollmentPostponement}
                onChange={(e) =>
                  handleIsEnrollmentPostponementChange(e.target.checked)
                }
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("IsEnrollmentPostponement")
                  )?.message
                }
                plaintext={!isEditing}
                disabled={
                  !(
                    item.type === RequestType.RegularEnrollment ||
                    item.type ===
                      RequestType.RegularEnrollmentForChildrenWithLearningDifficulties
                  )
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Checkbox
                label={t("form7.isTemporaryExemptionApproved")}
                name={nameof<typeof form7>("isTemporaryExemptionApproved")}
                checked={form7.isTemporaryExemptionApproved}
                onChange={(e) =>
                  handleIsTemporaryExemptionApprovedChange(e.target.checked)
                }
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("IsTemporaryExemptionApproved")
                  )?.message
                }
                plaintext={!isEditing}
                disabled={item.type !== RequestType.TemporaryExemption}
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Checkbox
                label={t("form7.isTemporaryExemptionRejected")}
                name={nameof<typeof form7>("isTemporaryExemptionRejected")}
                checked={form7.isTemporaryExemptionRejected}
                onChange={(e) =>
                  handleIsTemporaryExemptionRejectedChange(e.target.checked)
                }
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("IsTemporaryExemptionRejected")
                  )?.message
                }
                plaintext={!isEditing}
                disabled={item.type !== RequestType.TemporaryExemption}
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Checkbox
                label={t("form7.isAppropriateProgram")}
                name={nameof<typeof form7>("isAppropriateProgram")}
                checked={form7.isAppropriateProgram}
                onChange={(e) =>
                  handleIsAppropriateProgramChange(e.target.checked)
                }
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("IsAppropriateProgram")
                  )?.message
                }
                plaintext={!isEditing}
                disabled={
                  !(
                    item.type === RequestType.RegularEnrollment ||
                    item.type ===
                      RequestType.RegularEnrollmentForChildrenWithLearningDifficulties
                  )
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Checkbox
                label={t("decisionProposal.isPreparatoryCourse")}
                name={nameof<typeof form7>("isPreparatoryCourse")}
                checked={form7.isPreparatoryCourse}
                onChange={(e) =>
                  handleIsPreparatoryCourseChange(e.target.checked)
                }
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("IsPreparatoryCourse")
                  )?.message
                }
                plaintext={!isEditing}
                disabled={
                  form7.isEarlyEnrollmentRejected ||
                  form7.isEnrollmentPostponement ||
                  form7.isTemporaryExemptionApproved ||
                  form7.isTemporaryExemptionRejected
                }
              />
            </Col>
          </Row>
        </FormCard>
        {form7?.committeesForDocument &&
          initialDocumentCommittees &&
          !!initialDocumentCommittees.length && (
            <CommissionSelection
              committees={form7.committeesForDocument}
              onCommitteeChange={handleCommitteeChange}
              initialDocumentCommittees={initialDocumentCommittees}
              isEditing={isEditing}
            />
          )}
        <FormCard cardTitle={t("form7.institution")}>
          <Row>
            <Col md={12}>
              <InstitutionSelect
                label={t("common.expertCommission.institution")}
                name={nameof<typeof form7>("institutionId")}
                onlyByEnrollmentArea={false}
                excludeHandicapped={false}
                excludePrivate={false}
                excludeSatellite={false}
                value={form7.institutionId}
                showUser={false}
                countyId={
                  auth.currentRole?.claimType === ClaimType.Global
                    ? undefined
                    : item.countyId
                }
                allowEmpty={
                  !isEditing ||
                  form7.isEarlyEnrollmentRejected ||
                  form7.isEnrollmentPostponement ||
                  form7.isTemporaryExemptionApproved ||
                  form7.isTemporaryExemptionRejected
                }
                ids={
                  item.type === RequestType.EarlyEnrollment ||
                  !form7.isAppropriateProgram
                    ? item.isOutsideEnrollmentArea
                      ? [
                          item.outsideEnrollmentAreaInstitutionProgram!
                            .institutionId,
                          item.institutionId!,
                        ]
                      : [item.institutionId!]
                    : undefined
                }
                onChange={(e) => handleInstitutionChange(e.target.value)}
                disabled={
                  !isEditing ||
                  form7.isEarlyEnrollmentRejected ||
                  form7.isEnrollmentPostponement ||
                  form7.isTemporaryExemptionApproved ||
                  form7.isTemporaryExemptionRejected
                }
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("InstitutionId")
                  )?.message
                }
              />
            </Col>
          </Row>
        </FormCard>
        <FormCard>
          <Row>
            <Col md={6}>
              <Checkbox
                label={t("form7.hasDifficulties")}
                name={nameof<typeof form7>("hasDifficulties")}
                checked={form7.hasDifficulties}
                onChange={(e) => handleHasDifficultiesChange(e.target.checked)}
                plaintext={!isEditing}
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <TextField
                label={t("form7.difficultyDescription")}
                name={nameof<typeof form7>("difficultyDescription")}
                value={form7.difficultyDescription}
                onChange={(e) =>
                  handleDifficultyDescriptionChange(e.target.value)
                }
                disabled={isEditing && !form7.hasDifficulties}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("DifficultyDescription")
                  )?.message
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <TextField
                label={t("form7.rules")}
                name={nameof<typeof form7>("rules")}
                value={form7.rules}
                onChange={(e) => handleRulesChange(e.target.value)}
                disabled={isEditing && !form7.hasDifficulties}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) => e.propertyName?.startsWith("Rules"))
                    ?.message
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <TextField
                label={t("form7.nNNumber")}
                name={nameof<typeof form7>("nnNumber")}
                value={form7.nnNumber}
                onChange={(e) => handleNNNumberChange(e.target.value)}
                disabled={isEditing && !form7.hasDifficulties}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) => e.propertyName?.startsWith("NNNumber"))
                    ?.message
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <TextField
                label={t("form7.schoolingDescription")}
                name={nameof<typeof form7>("schoolingDescription")}
                value={form7.schoolingDescription}
                onChange={(e) =>
                  handleSchoolingDescriptionChange(e.target.value)
                }
                disabled={isEditing && !form7.hasDifficulties}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("SchoolingDescription")
                  )?.message
                }
              />
            </Col>
          </Row>
        </FormCard>
        <FormCard cardTitle={t("form7.notes")}>
          <Row>
            <Col md={12}>
              <TextField
                label={t("form7.educationalStatus")}
                name={nameof<typeof form7>("educationalStatus")}
                value={form7.educationalStatus}
                onChange={(e) => handleEducationalStatusChange(e.target.value)}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("EducationalStatus")
                  )?.message
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <TextField
                label={t("form7.appropriateHelpFormat")}
                name={nameof<typeof form7>("appropriateHelpFormat")}
                value={form7.appropriateHelpFormat}
                onChange={(e) =>
                  handleAppropriateHelpFormatChange(e.target.value)
                }
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) =>
                    e.propertyName?.startsWith("AppropriateHelpFormat")
                  )?.message
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Select
                label={t("form7.difficultyTypes")}
                name={nameof<typeof form7>("difficultyTypes")}
                value={form7.difficultyTypes}
                onChange={(e) => handleDifficultyTypeChange(e.target.value)}
                selectData={codeBookToSelectData(difficultyTypes)}
                errorText={
                  errors?.find((e) => e.propertyName === "DifficultyTypes")
                    ?.message
                }
                disabled={!isEditing}
                multiple
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <TextField
                label={t("form7.other")}
                name={nameof<typeof form7>("other")}
                value={form7.other}
                onChange={(e) => handleOtherChange(e.target.value)}
                plaintext={!isEditing}
                errorText={
                  errors?.find((e) => e.propertyName?.startsWith("Other"))
                    ?.message
                }
              />
            </Col>
          </Row>
        </FormCard>
        {!isEditing && form7.attachment && (
          <DocumentList
            documents={form7.attachment ? [form7.attachment] : []}
            onDocumentClick={(doc) =>
              handleDocumentDownload(doc, DocumentType.Form7)
            }
            removable={false}
          />
        )}
      </FormCard>
      <ToolbarEdit
        showCancel={"show"}
        showSave={
          isEditing &&
          (auth.currentRole?.role === Role.CAS ||
            auth.currentRole?.role === Role.Helpdesk ||
            auth.currentRole?.role === Role.CountyAdministrator ||
            auth.currentRole?.role === Role.CountyLeader ||
            (item.countyCommitteeId &&
              auth
                .getCommitteeMembershipByCommitteeId(item.countyCommitteeId)
                ?.some((v) => v.isPresident)))
            ? "show"
            : "hide"
        }
        redirectUrlSaveOK={() => requestAdminDetailsUrl(+id)}
        handleSave={async () => saveForm7({ ...form7, request: item })}
        setSaveErrors={setErrors}
        showCustom={false}
      />
    </ContentArea>
  );
}
